import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import store, { RootState } from '../store';
import { logout, setCredentials } from '../store/slices/authSlice';
import type { Plan, PlanLimitKey } from '../types/Restaurant';

const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const api = axios.create({ baseURL });

api.interceptors.request.use(
    (config) => {
        const token = (store.getState() as RootState).auth.token;
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

// A 401 on these is an answer, not an expired session.
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout'];

let refreshing: Promise<string> | null = null;

const endSession = () => {
    store.dispatch(logout());
    window.location.assign('/login');
};

/** One refresh request is shared by every request that gets a 401 while it is in flight. */
const refreshAccessToken = (): Promise<string> => {
    if (!refreshing) {
        const refreshToken = (store.getState() as RootState).auth.refreshToken;
        if (!refreshToken) return Promise.reject(new Error('No refresh token'));
        refreshing = axios
            .post<{ access_token: string; refresh_token: string }>(`${baseURL}/auth/refresh`, { refreshToken })
            .then(({ data }) => {
                store.dispatch(setCredentials({ token: data.access_token, refreshToken: data.refresh_token }));
                return data.access_token;
            })
            .finally(() => {
                refreshing = null;
            });
    }
    return refreshing;
};

api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const original = error.config as RetriableRequest | undefined;

        if (
            error.response?.status !== 401 ||
            !original ||
            original._retry ||
            AUTH_PATHS.some((path) => original.url?.startsWith(path))
        ) {
            return Promise.reject(error);
        }
        original._retry = true;

        try {
            const token = await refreshAccessToken();
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
        } catch (refreshError) {
            // Only a rejected refresh token ends the session; an outage must not sign people out.
            const status = axios.isAxiosError(refreshError) ? refreshError.response?.status : undefined;
            if (status === undefined && axios.isAxiosError(refreshError)) return Promise.reject(error);
            endSession();
            return Promise.reject(error);
        }
    }
);

export const updatePassword = (data: { currentPassword: string; newPassword: string }) => {
    return api.put('/users/me/password', data);
};

export interface ApiError {
    status: number | null;
    code: string | null;
    /** Server message; empty when the API sent none. Callers fall back to a translated generic text. */
    message: string;
    limit: PlanLimitKey | null;
    max: number | null;
    plan: Plan | null;
    items: Array<string | { id: string; name: string }>;
}

interface ErrorBody {
    code?: unknown;
    message?: unknown;
    limit?: unknown;
    max?: unknown;
    plan?: unknown;
    items?: unknown;
}

export const getApiError = (e: unknown): ApiError => {
    const empty: ApiError = { status: null, code: null, message: '', limit: null, max: null, plan: null, items: [] };
    if (!axios.isAxiosError(e)) return empty;
    const body: ErrorBody = e.response?.data && typeof e.response.data === 'object' ? e.response.data : {};
    const message = Array.isArray(body.message) ? body.message.join('; ') : body.message;
    return {
        status: e.response?.status ?? null,
        code: typeof body.code === 'string' ? body.code : null,
        message: typeof message === 'string' ? message : '',
        limit: body.limit === 'locations' || body.limit === 'users' || body.limit === 'languages' ? body.limit : null,
        max: typeof body.max === 'number' ? body.max : null,
        plan: typeof body.plan === 'string' ? (body.plan as Plan) : null,
        items: Array.isArray(body.items) ? body.items : [],
    };
};

export interface PlanLimitError extends ApiError {
    code: 'PLAN_LIMIT';
    limit: PlanLimitKey;
    max: number;
    plan: Plan;
}

/** Returns the typed 403 PLAN_LIMIT body, or null for any other error. */
export const asPlanLimit = (e: unknown): PlanLimitError | null => {
    const err = getApiError(e);
    return err.status === 403 && err.code === 'PLAN_LIMIT' && err.limit && err.max !== null && err.plan
        ? { ...err, code: 'PLAN_LIMIT', limit: err.limit, max: err.max, plan: err.plan }
        : null;
};

export default api;
